import { ChangeDetectionStrategy, Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AnalystColumnType, AnalystTable, Citation } from 'src/app/models/analyst-output.model';
import { DateFormatService } from 'src/app/services/dateFormat.service';
import { CitationPanelService } from 'src/app/services/citation-panel.service';
import { formatAnalystCell } from 'src/app/shared/utils/analyst-cell-format.util';
import { capCitationMarkers, CitationMarker, CitationNumbering, EMPTY_CITATION_NUMBERING, resolveCitationMarkers } from 'src/app/shared/utils/citation-resolver.util';
import { trackByIndex } from 'src/app/shared/utils/track-by.util';

interface AnalystTableRowVm {
  formattedCells: string[];
  markers: CitationMarker[];
  visibleMarkers: CitationMarker[];
  hiddenMarkers: CitationMarker[];
}

@Component({
  standalone: false,
  selector: 'app-analyst-table',
  templateUrl: './analyst-table.component.html',
  styleUrls: ['./analyst-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AnalystTableComponent implements OnChanges {
  @Input() table?: AnalystTable;
  @Input() citationNumbering: CitationNumbering = EMPTY_CITATION_NUMBERING;

  readonly AnalystColumnType = AnalystColumnType;
  readonly trackByIndex = trackByIndex;

  rows: AnalystTableRowVm[] = [];

  constructor(
    private readonly dateFormatService: DateFormatService,
    private readonly translate: TranslateService,
    private readonly citationPanelService: CitationPanelService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['table'] || changes['citationNumbering']) {
      this.recompute();
    }
  }

  private recompute(): void {
    if (!this.table) {
      this.rows = [];
      return;
    }

    const columns = this.table.columns;
    this.rows = this.table.rows.map((row) => {
      const markers = resolveCitationMarkers(row.citationIds, this.citationNumbering);
      const { visible, hidden } = capCitationMarkers(markers);
      return {
        formattedCells: row.cells.map((cell, cellIndex) => this.formatCell(cell, columns[cellIndex]?.dataType)),
        markers,
        visibleMarkers: visible,
        hiddenMarkers: hidden,
      };
    });
  }

  onMarkerClick(event: MouseEvent, citation: Citation): void {
    this.citationPanelService.openMarkerFromClick(event, citation);
  }

  private formatCell(value: string, dataType: AnalystColumnType | undefined): string {
    const resolvedType = dataType ?? AnalystColumnType.Text;
    const formatted = formatAnalystCell(value, resolvedType, (isoValue) =>
      this.dateFormatService.formatDateTimeToString(new Date(isoValue), this.dateFormatService.userDateFormat),
    );
    return resolvedType === AnalystColumnType.Boolean ? this.translate.instant(formatted) : formatted;
  }
}
